"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getApiBase } from "@/lib/api";

const API_BASE = getApiBase();

interface User {
  id: string;
  username: string;
  display_name?: string;
  email?: string;
  avatar?: string;
  role: string;
  status: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  needSetup: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (username: string, password: string, displayName?: string) => Promise<{ success: boolean; message?: string; needApproval?: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needSetup, setNeedSetup] = useState(false);

  // 初始化：检查系统状态和已保存的 token
  useEffect(() => {
    const init = async () => {
      try {
        // 检查系统是否需要初始化
        const checkRes = await fetch(`${API_BASE}/api/auth/check`);
        const checkData = await checkRes.json();
        setNeedSetup(checkData.need_setup);

        // 如果有保存的 token，尝试获取用户信息
        const savedToken = localStorage.getItem("token");
        if (savedToken) {
          setToken(savedToken);
          try {
            const meRes = await fetch(`${API_BASE}/api/auth/me`, {
              headers: { Authorization: `Bearer ${savedToken}` }
            });
            if (meRes.ok) {
              const meData = await meRes.json();
              setUser(meData.user);
            } else {
              // Token 无效，清除
              localStorage.removeItem("token");
              setToken(null);
            }
          } catch {
            localStorage.removeItem("token");
            setToken(null);
          }
        }
      } catch (error) {
        console.error("Auth init failed:", error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();

      if (!res.ok) {
        return { success: false, message: data.detail || "登录失败" };
      }

      // 保存 token 和用户信息
      localStorage.setItem("token", data.token);
      setToken(data.token);
      setUser(data.user);
      setNeedSetup(false);

      return { success: true };
    } catch (error) {
      return { success: false, message: "网络错误" };
    }
  };

  const register = async (username: string, password: string, displayName?: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, display_name: displayName })
      });
      const data = await res.json();

      if (!res.ok) {
        return { success: false, message: data.detail || "注册失败" };
      }

      // 如果是第一个用户，直接登录
      if (data.token) {
        localStorage.setItem("token", data.token);
        setToken(data.token);
        setUser(data.user);
        setNeedSetup(false);
        return { success: true };
      }

      // 否则需要等待审批
      return { success: true, needApproval: true, message: data.message };
    } catch (error) {
      return { success: false, message: "网络错误" };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      }
    } catch (error) {
      console.error("Refresh user failed:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, needSetup, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
